/**
 * API client for the Dashboard page.
 */

import { ApiCaller } from 'apis/ApiCaller';

// ── ETileType values (from GMetrix.Data/Entities/Enums/ETileType.cs) ──

export const ETileType = {
    JasperTile: 11,
    MessagingTile: 9,
    NewsTile: 4,
    PromoTile: 1,
    QuickLinksTile: 2,
    QuickReportsTile: 26,
    ResourcesTile: 6
} as const;

// ── Response types ──

export type Promotion = {
    BannerImageURL: string;
    Index: number;
    OpenInNewTab: number;
    PageURL: string;
    Title: string;
};

export type Message = {
    Active: boolean;
    CreateDate: string;
    EndDate: string;
    Id: number;
    MessageId: number;
    PartnerId: number;
    PartnerName: string;
    StartDate: string;
    Text: string;
    UserId: number;
};

export type TileLink = {
    Id: number;
    LinkUrl: string;
    Name: string;
};

export type TileLinkSection = {
    Id: number;
    Links: Record<number, TileLink>;
    Name: string;
};

export type JasperInfo = {
    MsgSuccess: string;
    Successful: boolean;
};

export type PartnerVendorInfo = {
    jasper: JasperInfo | null;
    vendors: string[];
};

// ── API methods ──

export class DashboardApi {

    private static apiCaller = new ApiCaller('Dashboard');

    public static GetPromos(userId: number, roleId: number, partnerId: number): Promise<Record<number, Promotion>> {

        return this.apiCaller.POST<Record<number, Promotion>>('GetTileData', {
            partnerId,
            roleId,
            tileId: ETileType.PromoTile,
            userId
        });

    }

    public static GetMessages(userId: number, roleId: number, partnerId: number): Promise<Message[]> {

        return this.apiCaller.POST<Message[]>('GetTileData', {
            partnerId,
            roleId,
            tileId: ETileType.MessagingTile,
            userId
        });

    }

    public static GetQuickLinks(userId: number, roleId: number, partnerId: number): Promise<Record<number, TileLinkSection>> {

        return this.apiCaller.POST<Record<number, TileLinkSection>>('GetTileData', {
            partnerId,
            roleId,
            tileId: ETileType.QuickLinksTile,
            userId
        });

    }

    public static GetQuickReports(userId: number, roleId: number, partnerId: number): Promise<Record<number, TileLinkSection>> {

        return this.apiCaller.POST<Record<number, TileLinkSection>>('GetTileData', {
            partnerId,
            roleId,
            tileId: ETileType.QuickReportsTile,
            userId
        });

    }

    public static GetResources(userId: number, roleId: number, partnerId: number): Promise<Record<number, TileLinkSection>> {

        return this.apiCaller.POST<Record<number, TileLinkSection>>('GetTileData', {
            partnerId,
            roleId,
            tileId: ETileType.ResourcesTile,
            userId
        });

    }

    public static async RemoveMessage(userId: number, messageId: number): Promise<void> {

        await this.apiCaller.POST<unknown>('RemoveMessage', {
            messageId,
            userId
        });

    }

    public static async GetApiKeyStatus(partnerId: number): Promise<boolean> {

        const result = await this.apiCaller.GET<{ hasKey: boolean }>('GetApiKeyByPartnerId', { partnerId });
        return result.hasKey;

    }

    public static GetPartnerVendorInfo(userId: number, partnerId: number): Promise<PartnerVendorInfo> {

        return this.apiCaller.GET<PartnerVendorInfo>('GetPartnerVendorInfo', { partnerId, userId });

    }

    public static async GetMissingClaims(): Promise<string[]> {

        const result = await this.apiCaller.GET<string[]>('GetMissingClaims');
        return result ?? [];

    }

    public static async UpdateClaims(optIn: boolean): Promise<void> {

        await this.apiCaller.POST<unknown>('UpdateClaims', { optIn });

    }

}
